import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { formatAuctionEnd } from "../lib/formatters";

interface CountdownTimerProps {
  endsAt: string;
}

export function CountdownTimer({ endsAt }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    const distance = new Date(endsAt).getTime() - new Date().getTime();
    return Math.max(0, distance);
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const end = new Date(endsAt).getTime();
      const distance = end - now;

      if (distance <= 0) {
        clearInterval(timer);
        setTimeLeft(0);
      } else {
        setTimeLeft(distance);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endsAt]);

  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const showFormattedEnd = timeLeft > TWO_HOURS_MS;

  if (showFormattedEnd) {
    return (
      <div className="flex items-center gap-1.5 font-bold text-slate-600">
        <Clock className="h-4 w-4" />
        <span className="text-xs uppercase tracking-tight">{formatAuctionEnd(endsAt)}</span>
      </div>
    );
  }

  if (timeLeft <= 0) {
    return (
      <div className="flex items-center gap-1.5 font-mono font-bold text-muted-foreground">
        <Clock className="h-4 w-4" />
        <span>Avsluttet</span>
      </div>
    );
  }

  const h = Math.floor(timeLeft / (1000 * 60 * 60));
  const m = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const s = Math.floor((timeLeft % (1000 * 60)) / 1000);

  const parts = [];
  if (h > 0) parts.push(`${h}t`);
  if (m > 0 || h > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);

  const isEndingSoon = timeLeft < 5 * 60 * 1000;

  return (
    <div className={`flex items-center gap-1.5 font-mono font-bold ${isEndingSoon ? "text-red-500 animate-pulse" : "text-amber-600"}`}>
      <Clock className="h-4 w-4" />
      <span>{parts.join(" ")}</span>
    </div>
  );
}
